import {otsBaseApi} from "../../ots-api";
import {CustomFieldDef} from "../../../model/custom-field-def";

const ALL_TAG_TYPES = [
  "CustomFieldDefs",
  "CompanyCustomFieldDefs",
  "CustomFieldProfileCatalogItems",
  "CompanyCustomFieldProfileCatalogItems",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const customFieldDefApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCustomFieldDefs: builder.query<
      CustomFieldDef[],
      void
    >({
      query: () => ({
        url: `/custom-field-defs/all`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldDefs", id} as const)),
            {type: "CustomFieldDefs", id: "LIST"}
          ];
        } else {
          return [{type: "CustomFieldDefs", id: "LIST"}];
        }
      },
    }),
    getCustomFieldDefsByCompany: builder.query<
      CustomFieldDef[],
      string
    >({
      query: (arg) => ({
        url: `/custom-field-defs/company/${arg}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "CustomFieldDefs", id} as const)),
            {type: "CompanyCustomFieldDefs", id: arg}
          ];
        } else {
          return [{type: "CompanyCustomFieldDefs", id: arg}];
        }
      },
    }),
    getCustomFieldDef: builder.query<CustomFieldDef, string>({
      query: (id) => ({
        url: `/custom-field-defs/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "CustomFieldDefs", id}]
    }),
    createCustomFieldDef: builder.mutation<CustomFieldDef, Partial<CustomFieldDef>>({
      query: (mapping) => ({
        url: "/custom-field-defs",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: (result) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "CustomFieldDefs", id: "LIST"})

        if (result?.companyId) {
          tags.push({type: "CompanyCustomFieldDefs", id: result.companyId})
        }

        if (result?.companyId && result?.forObjectType) {
          tags.push({type: "CustomFieldProfileCatalogItems"})
          tags.push({type: "CompanyCustomFieldProfileCatalogItems", id: `${result.companyId}:${result.forObjectType}`})
        }

        return tags
      }
    }),
    updateCustomFieldDef: builder.mutation<CustomFieldDef, Partial<CustomFieldDef>>({
      query: (mapping) => ({
        url: "/custom-field-defs",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, { id, companyId, forObjectType }) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "CustomFieldDefs", id})
        tags.push({type: "ObjectObjectChangeLogs", id})

        if (companyId) {
          tags.push({type: "CompanyCustomFieldDefs", id: companyId})
        }

        if (companyId && forObjectType) {
          tags.push({type: "CustomFieldProfileCatalogItems"})
          tags.push({type: "CompanyCustomFieldProfileCatalogItems", id: `${companyId}:${forObjectType}`})
        }

        return tags
      }
    }),
    deleteCustomFieldDef: builder.mutation<void, Partial<CustomFieldDef>>({
      query: (mapping) => ({
        url: "/custom-field-defs",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "CustomFieldDefs", id},
        {type: "CompanyCustomFieldDefs"},
        {type: "CustomFieldProfileCatalogItems"},
        {type: "CompanyCustomFieldProfileCatalogItems"},
      ]
    }),
  })
});

export const {
  useGetAllCustomFieldDefsQuery,
  useGetCustomFieldDefsByCompanyQuery,
  useGetCustomFieldDefQuery,
  useCreateCustomFieldDefMutation,
  useUpdateCustomFieldDefMutation,
  useDeleteCustomFieldDefMutation,
} = customFieldDefApi;
